/* H&M PDP — zoom dialog prev/next arrows (desktop).
 *
 * Horizon's zoom dialog (<zoom-dialog>) ships no side arrows. The Figma design
 * shows ‹ › buttons on the far left/right of the desktop lightbox, using the
 * icon-arrow-left / icon-arrow-right glyphs. This enhancer injects those
 * buttons and wires them to page horizontally (prev/next) through the gallery,
 * without touching the core zoom-dialog component. Mobile keeps the native
 * swipe, so the arrows are hidden there via CSS. */

const ARROW_PREV =
  '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path fill="currentColor" d="m0 8 7.879 7.879 1.06-1.061L2.871 8.75h13.13v-1.5H2.87L8.94 1.182 7.88.122z"/></svg>';
const ARROW_NEXT =
  '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path fill="currentColor" d="M16.0005 7.99989L8.12192 0.121094L7.06126 1.18175L13.1294 7.2499L0 7.24989V8.74989L13.1294 8.7499L7.06126 14.818L8.12192 15.8787L16.0005 7.99989Z"/></svg>';

/**
 * Inject and wire the prev/next arrows for a single zoom dialog.
 * @param {HTMLElement} zoom - The <zoom-dialog> element.
 */
function enhance(zoom) {
  if (zoom.dataset.hmArrows) return;

  const dialog = zoom.querySelector('.dialog-zoomed-gallery__dialog');
  const gallery = zoom.querySelector('.dialog-zoomed-gallery');
  const media = Array.from(zoom.querySelectorAll('.dialog-zoomed-gallery > li'));
  if (!dialog || !gallery || media.length < 2) return;

  zoom.dataset.hmArrows = 'true';

  const makeArrow = (dir) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `hm-zoom-arrow hm-zoom-arrow--${dir}`;
    button.setAttribute('aria-label', dir === 'prev' ? 'Previous image' : 'Next image');
    button.innerHTML = dir === 'prev' ? ARROW_PREV : ARROW_NEXT;
    return button;
  };

  const prev = makeArrow('prev');
  const next = makeArrow('next');
  dialog.append(prev, next);

  const scrollTo = (index) =>
    media[index]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });

  // Pagination dots (centred at the bottom, like the production site).
  const dotsWrap = document.createElement('div');
  dotsWrap.className = 'hm-zoom-dots';
  const dots = media.map((_, i) => {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'hm-zoom-dot';
    dot.setAttribute('aria-label', `Go to image ${i + 1}`);
    dot.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      scrollTo(i);
    });
    dotsWrap.append(dot);
    return dot;
  });
  dialog.append(dotsWrap);

  /** Index of the media item currently closest to the gallery centre. */
  const currentIndex = () => {
    const box = gallery.getBoundingClientRect();
    const cx = box.left + box.width / 2;
    let best = 0;
    let bestDist = Infinity;
    media.forEach((el, i) => {
      const r = el.getBoundingClientRect();
      const dist = Math.abs(r.left + r.width / 2 - cx);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    return best;
  };

  const go = (delta) => {
    scrollTo(Math.min(media.length - 1, Math.max(0, currentIndex() + delta)));
  };

  const update = () => {
    const i = currentIndex();
    prev.disabled = i <= 0;
    next.disabled = i >= media.length - 1;
    dots.forEach((dot, idx) => dot.setAttribute('aria-current', String(idx === i)));
  };

  for (const [button, delta] of [
    [prev, -1],
    [next, 1],
  ]) {
    button.addEventListener('click', (event) => {
      // The image tiles close the dialog on click; keep arrow clicks isolated.
      event.preventDefault();
      event.stopPropagation();
      go(delta);
    });
  }

  // The <ul> is the horizontal scroll container; refresh state on scroll/swipe.
  gallery.addEventListener('scroll', update, { passive: true });

  // Refresh once the dialog actually opens and layout has settled.
  const observer = new MutationObserver(() => {
    if (dialog.open) requestAnimationFrame(update);
  });
  observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });

  update();
}

function init() {
  document.querySelectorAll('zoom-dialog').forEach((el) => enhance(/** @type {HTMLElement} */ (el)));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Re-run when a section is re-rendered in the theme editor.
document.addEventListener('shopify:section:load', init);
